import { Link } from 'react-router-dom';

const LOGO_URL =
  'https://storage.readdy-site.link/project_files/66c63773-f4df-4b51-a232-b85a9487d8fb/a97ed8bf-79aa-4d6a-a5e7-b55b88780d52_compressed_Penland-LLC-Logo.webp';

const divisionLinks = [
  { label: 'Home', to: '/' },
  { label: 'Penland Bookkeeping', to: '/bookkeeping' },
  { label: 'Consulting & Web Development', to: '/consulting' },
  { label: 'Penland Properties', to: '/properties' },
  { label: 'MatchLineup', to: '/matchlineup' },
  { label: 'Alabaster Travel', to: 'https://alabastertravel.com/', external: true },
];

const legalLinks = [
  { label: 'Privacy Policy', href: 'https://penlandllc.com/legal/privacy.html' },
  { label: 'Terms of Service', href: 'https://penlandllc.com/legal/terms.html' },
  { label: 'Bookkeeping Bot Privacy', href: 'https://penlandllc.com/legal/bookkeeping-bot-privacy.html' },
  { label: 'Bookkeeping Bot Terms', href: 'https://penlandllc.com/legal/bookkeeping-bot-terms.html' },
  { label: 'SMS Terms', href: 'https://penlandllc.com/legal/sms.html' },
];

const bookkeepingLinks = [
  { label: 'Monthly Bookkeeping', href: '#services' },
  { label: 'Payroll & Compliance', href: '#services' },
  { label: 'Job Costing', href: '#services' },
  { label: 'Reporting & Advisory', href: '#services' },
];

const industriesLinks = [
  { label: 'Restaurants & Bars', href: '#industries' },
  { label: 'Cafes & Catering', href: '#industries' },
  { label: 'Electricians & Plumbers', href: '#industries' },
  { label: 'HVAC & Contractors', href: '#industries' },
  { label: 'Small Business', href: '#industries' },
  { label: 'Nonprofits', href: '#industries' },
];

const areasLinks = [
  { label: 'Richmond', href: '#areas' },
  { label: 'Henrico County', href: '#areas' },
  { label: 'Chesterfield County', href: '#areas' },
];

const resourcesLinks = [
  { label: 'Free Checklist', href: '#resources' },
  { label: 'Blog & Insights', href: '#resources' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Discovery Call', href: '#contact' },
];

interface FooterColumn {
  title: string;
  type: 'route' | 'external' | 'anchor';
  links: { label: string; to?: string; href?: string; external?: boolean }[];
}

export default function SiteFooter({ variant = 'default' }: { variant?: 'default' | 'bookkeeping' }) {
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const columns: FooterColumn[] = variant === 'bookkeeping'
    ? [
        { title: 'Services', type: 'anchor', links: bookkeepingLinks },
        { title: 'Industries', type: 'anchor', links: industriesLinks },
        { title: 'Areas Served', type: 'anchor', links: areasLinks },
        { title: 'Resources', type: 'anchor', links: resourcesLinks },
        { title: 'Divisions', type: 'route', links: divisionLinks.map((d) => ({ label: d.label, to: d.to, external: d.external })) },
        { title: 'Legal', type: 'external', links: legalLinks },
      ]
    : [
        { title: 'Divisions', type: 'route', links: divisionLinks.map((d) => ({ label: d.label, to: d.to, external: d.external })) },
        { title: 'Legal', type: 'external', links: legalLinks },
      ];

  const gridClass =
    columns.length > 4
      ? 'grid-cols-2 md:grid-cols-3'
      : 'grid-cols-2 sm:grid-cols-2';

  return (
    <footer className="bg-background-950 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 mb-12 md:mb-16">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-5">
                <img
                  src={LOGO_URL}
                  alt="Penland LLC logo"
                  className="h-10 w-auto object-contain brightness-0 invert opacity-90"
                />
                <span className="font-heading text-xl font-semibold text-background-50">
                  Penland LLC
                </span>
              </div>
              <p className="text-background-400 text-sm leading-relaxed mb-6 max-w-sm">
                A Virginia company that owns and operates a small group of businesses in
                bookkeeping, consulting and web development, property management, software,
                and travel.
              </p>
              <div className="space-y-2 mb-6">
                <a
                  href="mailto:info@penlandllc.com"
                  className="flex items-center gap-2 text-background-300 hover:text-accent-400 text-sm transition-colors"
                >
                  <i className="ri-mail-line text-accent-400" />
                  info@penlandllc.com
                </a>
                <a
                  href="tel:+18044647418"
                  className="flex items-center gap-2 text-background-300 hover:text-accent-400 text-sm transition-colors"
                >
                  <i className="ri-phone-line text-accent-400" />
                  (804) 464-7418
                </a>
                <p className="flex items-start gap-2 text-background-400 text-sm">
                  <i className="ri-map-pin-line text-accent-400 mt-0.5" />
                  PO Box 432, Midlothian, VA 23113
                </p>
                <p className="flex items-start gap-2 text-background-400 text-sm">
                  <i className="ri-time-line text-accent-400 mt-0.5" />
                  Mon&ndash;Fri, 9:00 a.m.&ndash;5:00 p.m. Eastern
                </p>
              </div>
            </div>

            <div className={`lg:col-span-8 grid ${gridClass} gap-8`}>
              {columns.map((col) => (
                <div key={col.title}>
                  <h4 className="text-background-50 text-sm font-semibold mb-4">
                    {col.title}
                  </h4>
                  <ul className="space-y-2.5">
                    {col.links.map((link) => (
                      <li key={link.label}>
                        {link.external && link.to ? (
                          <a
                            href={link.to}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-background-400 hover:text-accent-400 text-sm transition-colors"
                          >
                            {link.label}
                          </a>
                        ) : col.type === 'route' && link.to ? (
                          <Link
                            to={link.to}
                            className="text-background-400 hover:text-accent-400 text-sm transition-colors"
                          >
                            {link.label}
                          </Link>
                        ) : col.type === 'external' && link.href ? (
                          <a
                            href={link.href}
                            className="text-background-400 hover:text-accent-400 text-sm transition-colors"
                          >
                            {link.label}
                          </a>
                        ) : (
                          <button
                            onClick={() => scrollTo(link.href || '#')}
                            className="text-background-400 hover:text-accent-400 text-sm transition-colors text-left"
                          >
                            {link.label}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-background-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-background-500 text-xs md:text-sm text-center md:text-left max-w-3xl">
                &copy; 2026 Penland LLC. Penland Bookkeeping, Penland Properties, MatchLineup,
                and Alabaster Travel are trade names of Penland LLC.
              </p>
              <Link
                to="/"
                className="text-background-500 hover:text-accent-400 text-xs md:text-sm transition-colors whitespace-nowrap"
              >
                Penland LLC
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}